import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { authStore, emailStore, UserProps } from '../store/auth';
import { UserSettings } from '@/types/userSettings';
import { UserStatusCodeType } from '@/types/codeType';
import { Masters } from '@/consts/masters';

type UseAuthContext = {
  signIn: () => void;
  signOut: () => void;
  refreshSettings: (settings: UserSettings) => void;
  user?: UserProps;
  isInitializing: boolean;
  isActionRequired: boolean;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  isRequiredInitialPayment: boolean;
  isRequiredSubscriptionPayment: boolean;
  isGuest: boolean;
};

// TODO: 全体的にuseEffectの最小化したい
// https://beta.reactjs.org/learn/you-might-not-need-an-effect
const useAuth = (): UseAuthContext => {
  const [auth, setAuth] = useRecoilState(authStore);
  // INFO: auth.userに定義するとステート管理が難しいため独立して管理
  const [_, setEmail] = useRecoilState(emailStore);

  const signIn = useCallback(async () => {
    if (auth.status === 'authenticating') {
      return;
    }

    setAuth({
      status: 'authenticating',
    });

    try {
      const res = await fetch(`/api/users/me`);
      if (!res.ok) {
        throw res;
      }

      const result = await res.json();
      const userStatus = result.status as UserStatusCodeType;
      setAuth({
        status:
          userStatus === Masters.UserStatusCode.VALID_UNREGISTERED
            ? 'requiredInitialPayment'
            : userStatus === Masters.UserStatusCode.INVALID_EXPIRED
            ? 'requiredSubscriptionPayment'
            : 'authenticated',
        user: {
          uid: result.uid as string,
          status: result.status as UserStatusCodeType,
          settings: result.settings as UserSettings,
        },
      });

      await fetch(`/api/users/signedIn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (e) {
      await signOut();
    }
  }, []);

  const signOut = useCallback(async () => {
    if (auth.status === 'none') {
      return;
    }
    setAuth({
      status: 'none',
    });
    setEmail(undefined);
  }, []);

  const refreshSettings = useCallback((settings: UserSettings) => {
    if (!auth.user) return;
    setAuth({
      status: auth.status,
      user: {
        uid: auth.user.uid,
        status: auth.user.status,
        settings: settings,
      },
    });
  }, []);

  return {
    signIn,
    signOut,
    refreshSettings,
    user: auth.user,
    isInitializing: auth.status === 'initializing',
    isActionRequired:
      auth.status === 'requiredInitialPayment' ||
      auth.status === 'requiredSubscriptionPayment',
    isAuthenticating: auth.status === 'authenticating',
    isAuthenticated: auth.status === 'authenticated',
    isRequiredInitialPayment: auth.status === 'requiredInitialPayment',
    isRequiredSubscriptionPayment: auth.status === 'requiredSubscriptionPayment',
    isGuest: auth.status === 'none',
  };
};

export default useAuth;
